import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import BASE_URL from '../config/config';
import { Link, useNavigate } from 'react-router-dom';
import ContentLoader from 'react-content-loader';
import { FiBell, FiRefreshCw, FiCheck, FiCheckCircle, FiInbox, FiX, FiAlertTriangle, FiTag } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import i18n from '../i18n';
import { cabecerasAuth, claves } from '../queries/cliente';

// Cada notificación no leída se refresca sola cada 5s. React Query deduplica
// las peticiones entre las instancias montadas (el Header monta el componente
// dos veces: barra de escritorio y barra del burger), así que no hace falta
// ningún store propio para evitar pollings en paralelo.
const INTERVALO_REFRESCO = 5000;

const sesionActual = () => {
  const token = localStorage.getItem('AuthToken');
  if (!token) return null;
  try {
    const { usuario, role } = jwtDecode(token);
    return usuario && role ? { usuario, role } : null;
  } catch (err) {
    return null;
  }
};

const Notificaciones = () => {
  const { t } = useTranslation();
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const sesion = sesionActual();

  const consultaTickets = useQuery({
    queryKey: claves.notificacionesTickets(sesion?.usuario),
    queryFn: async () => {
      const resp = await axios.get(`${BASE_URL}/ticket/notificaciones/${sesion.usuario}`, {
        params: { role: sesion.role },
      });
      return resp.data.tickets || [];
    },
    enabled: Boolean(sesion),
    refetchInterval: INTERVALO_REFRESCO,
    // El dato viejo se sigue pintando mientras se revalida: la campana no
    // parpadea a "cargando" cada cinco segundos.
    placeholderData: (anterior) => anterior,
  });

  const consultaAvisos = useQuery({
    queryKey: claves.notificaciones(sesion?.usuario),
    queryFn: async () => {
      const resp = await axios.get(`${BASE_URL}/notificaciones/${sesion.usuario}`, cabecerasAuth());
      return resp.data.notificaciones || [];
    },
    enabled: Boolean(sesion),
    refetchInterval: INTERVALO_REFRESCO,
    placeholderData: (anterior) => anterior,
  });

  const notificacionesTickets = consultaTickets.data || [];
  const notificacionesPersonalizadas = consultaAvisos.data || [];
  const isLoading = consultaTickets.isPending || consultaAvisos.isPending;
  const error = (consultaTickets.isError || consultaAvisos.isError)
    ? t('notificaciones.errorCargar')
    : null;

  const refrescar = () => {
    consultaTickets.refetch();
    consultaAvisos.refetch();
  };

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  // Fecha corta y relativa para las tarjetas ("hace 5 min", "ayer, 18:04"),
  // con la fecha completa en el title por si hace falta el detalle.
  const formatearFecha = (valor) => {
    const fecha = new Date(valor);
    if (Number.isNaN(fecha.getTime())) return '';

    const minutos = Math.round((Date.now() - fecha.getTime()) / 60000);
    if (minutos < 1) return t('notificaciones.ahoraMismo');
    if (minutos < 60) return t('notificaciones.haceMinutos', { count: minutos });
    if (minutos < 24 * 60) return t('notificaciones.haceHoras', { count: Math.floor(minutos / 60) });
    if (minutos < 48 * 60) {
      return t('notificaciones.ayerHora', { hora: new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }).format(fecha) });
    }
    return new Intl.DateTimeFormat(i18n.language, {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    }).format(fecha);
  };

  const fechaCompleta = (valor) => {
    const fecha = new Date(valor);
    if (Number.isNaN(fecha.getTime())) return '';
    return new Intl.DateTimeFormat(i18n.language, {
      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(fecha);
  };

  const obtenerClasePrioridad = (prioridad) => {
    const clases = {
      'urgente': 'prioridad-urgente',
      'alta': 'prioridad-alta',
      'normal': 'prioridad-normal',
      'baja': 'prioridad-baja'
    };
    return clases[prioridad] || clases['normal'];
  };

  const obtenerTextoPrioridad = (prioridad) => (
    t(`notificaciones.prioridades.${prioridad}`, { defaultValue: t('notificaciones.prioridades.normal') })
  );

  // La notificación desaparece de la lista en cuanto se pulsa (optimista) y,
  // si el servidor falla, react-query devuelve la lista anterior.
  const mutacionMarcarLeida = useMutation({
    mutationFn: (id) => axios.put(`${BASE_URL}/notificaciones/${id}/marcar-leida`, {}, cabecerasAuth()),
    onMutate: async (id) => {
      const clave = claves.notificaciones(sesion?.usuario);
      await queryClient.cancelQueries({ queryKey: clave });
      const previas = queryClient.getQueryData(clave);
      queryClient.setQueryData(clave, (actuales = []) => actuales.filter((n) => n.id !== id));
      return { previas, clave };
    },
    onError: (error, id, contexto) => {
      console.error('Error al marcar notificación como leída:', error);
      if (contexto) queryClient.setQueryData(contexto.clave, contexto.previas);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: claves.notificaciones(sesion?.usuario) });
    },
  });

  const marcarComoLeida = (id) => mutacionMarcarLeida.mutate(id);

  const manejarClickNotificacion = (notificacion) => {
    marcarComoLeida(notificacion.id);
    setIsDropdownOpen(false);

    if (notificacion.link) {
      if (notificacion.link.startsWith('/')) {
        navigate(notificacion.link);
      } else {
        window.open(notificacion.link, '_blank');
      }
    }
  };

  const mutacionMarcarTodas = useMutation({
    mutationFn: () => axios.put(`${BASE_URL}/notificaciones/${sesion.usuario}/marcar-todas-leidas`, {}, cabecerasAuth()),
    onMutate: async () => {
      const clave = claves.notificaciones(sesion?.usuario);
      await queryClient.cancelQueries({ queryKey: clave });
      const previas = queryClient.getQueryData(clave);
      queryClient.setQueryData(clave, []);
      return { previas, clave };
    },
    onError: (error, _vars, contexto) => {
      console.error('Error al marcar todas como leídas:', error);
      if (contexto) queryClient.setQueryData(contexto.clave, contexto.previas);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: claves.notificaciones(sesion?.usuario) });
    },
  });

  const marcarTodasComoLeidas = () => mutacionMarcarTodas.mutate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsDropdownOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const totalNotificaciones = notificacionesTickets.length + notificacionesPersonalizadas.length;

  // Cabecera compartida por los tres estados (cargando / error / lista) para
  // que el panel no cambie de altura ni de controles al refrescar.
  const renderCabecera = () => (
    <div className="cabecera-notificaciones">
      <div className="titulo-notificaciones">
        <h3>{t('notificaciones.titulo')}</h3>
        {totalNotificaciones > 0 && (
          <span className="contador-notificaciones">{totalNotificaciones}</span>
        )}
      </div>
      <div className="botones-cabecera">
        {notificacionesPersonalizadas.length > 0 && (
          <button
            className='boton-marcar-todas'
            onClick={marcarTodasComoLeidas}
            title={t('notificaciones.marcarTodasLeidas')}
          >
            <FiCheckCircle /> <span>{t('notificaciones.marcarTodoLeido')}</span>
          </button>
        )}
        <button
          className={`refresh-button ${isLoading ? 'cargando' : ''}`}
          onClick={refrescar}
          title={t('notificaciones.refrescar')}
          aria-label={t('notificaciones.refrescarNotificaciones')}
        >
          <FiRefreshCw />
        </button>
        {/* Solo visible en el drawer móvil: en escritorio se cierra clicando fuera */}
        <button
          className="cerrar-notificaciones"
          onClick={() => setIsDropdownOpen(false)}
          title={t('notificaciones.cerrar')}
          aria-label={t('notificaciones.cerrarNotificaciones')}
        >
          <FiX />
        </button>
      </div>
    </div>
  );

  return (
    <div className='modal-notificaciones' ref={dropdownRef}>
      <button
        className={`notificaciones ${isDropdownOpen ? 'active' : ''}`}
        onClick={toggleDropdown}
        aria-expanded={isDropdownOpen}
        aria-label={totalNotificaciones > 0 ? t('notificaciones.conSinLeer', { count: totalNotificaciones }) : t('notificaciones.titulo')}
      >
        <FiBell />
        {
          totalNotificaciones > 0 && (
            <div className='num-notificacion'>{totalNotificaciones > 99 ? '99+' : totalNotificaciones}</div>
          )
        }
      </button>

      {/* Fondo oscuro del drawer móvil (oculto en escritorio por CSS) */}
      <div
        className={`fondo-notificaciones ${isDropdownOpen ? 'open' : ''}`}
        onClick={() => setIsDropdownOpen(false)}
      />

      <div className={`dropdown-notificaciones ${isDropdownOpen ? 'open' : ''}`} role="dialog" aria-label={t('notificaciones.titulo')}>
        {isLoading ? (
          <div className="grid-notificaciones">
            {renderCabecera()}
            <div className="cuerpo-notificaciones">
              <ContentLoader height={160} width="100%" backgroundColor="#eef0f4" foregroundColor="#f8f9fb">
                <rect x="0" y="4" rx="8" ry="8" width="100%" height="38" />
                <rect x="0" y="48" rx="8" ry="8" width="100%" height="38" />
                <rect x="0" y="92" rx="8" ry="8" width="100%" height="38" />
                <rect x="0" y="136" rx="8" ry="8" width="100%" height="38" />
              </ContentLoader>
            </div>
          </div>
        ) : error ? (
          <div className="grid-notificaciones">
            {renderCabecera()}
            <div className="cuerpo-notificaciones">
              <div className="error-notificaciones">
                <FiAlertTriangle />
                <p>{error}</p>
                <button className="boton-reintentar" onClick={refrescar}>
                  {t('notificaciones.reintentar')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className='grid-notificaciones'>
            {renderCabecera()}

            <div className="cuerpo-notificaciones">
              {/* Notificaciones personalizadas */}
              {notificacionesPersonalizadas.length > 0 && (
                <div className="seccion-notificaciones">
                  <p className="subtitulo-notificaciones">{t('notificaciones.avisos')}</p>
                  {notificacionesPersonalizadas.map((notif) => {
                    const prioridadAlta = notif.prioridad === 'urgente' || notif.prioridad === 'alta';
                    return (
                    <div
                      key={notif.id}
                      className={`notificacion notificacion-personalizada ${notif.link ? 'clicable' : ''}`}
                      onClick={() => manejarClickNotificacion(notif)}
                    >
                      <span className={`notificacion-icono ${prioridadAlta ? obtenerClasePrioridad(notif.prioridad) : ''}`}>
                        <FiBell />
                      </span>
                      <div className="contenido-notificacion">
                        <div className="header-notificacion-contenido">
                          <p className='not-titulo'>{notif.titulo}</p>
                          {/* Solo se etiquetan las prioridades que piden atención:
                              "normal" y "baja" no aportan y ensucian la lista. */}
                          {prioridadAlta && (
                            <span className={`badge-prioridad ${obtenerClasePrioridad(notif.prioridad)}`}>
                              {obtenerTextoPrioridad(notif.prioridad)}
                            </span>
                          )}
                          <span className='not-fecha' title={fechaCompleta(notif.createdAt)}>
                            {formatearFecha(notif.createdAt)}
                          </span>
                        </div>
                        <p className='not-mensaje'>{notif.mensaje}</p>
                      </div>
                      <button
                        className="boton-marcar-leida"
                        onClick={(e) => {
                          e.stopPropagation();
                          marcarComoLeida(notif.id);
                        }}
                        title={t('notificaciones.marcarLeida')}
                        aria-label={t('notificaciones.marcarLeida')}
                      >
                        <FiCheck />
                      </button>
                    </div>
                    );
                  })}
                </div>
              )}

              {/* Notificaciones de tickets */}
              {notificacionesTickets.length > 0 && (
                <div className="seccion-notificaciones">
                  <p className="subtitulo-notificaciones">{t('notificaciones.tickets')}</p>
                  {notificacionesTickets.map((ticket, index) => {
                    const prioridadAlta = ticket.prioridad && ticket.prioridad !== 'normal' && ticket.prioridad !== 'baja';
                    return (
                    <Link
                      to={`/ticket/${ticket.identificador}`}
                      className="notificacion notificacion-ticket clicable"
                      key={index}
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <span className={`notificacion-icono ${prioridadAlta ? obtenerClasePrioridad(ticket.prioridad) : ''}`}>
                        <FiTag />
                      </span>
                      <div className="contenido-notificacion">
                        <div className="header-notificacion-contenido">
                          <p className='not-id'><b>#{ticket.identificador}</b> · {ticket.asunto}</p>
                          {prioridadAlta && (
                            <span className={`badge-prioridad ${obtenerClasePrioridad(ticket.prioridad)}`}>
                              {obtenerTextoPrioridad(ticket.prioridad)}
                            </span>
                          )}
                          <span className='not-fecha' title={fechaCompleta(ticket.updatedAt)}>
                            {formatearFecha(ticket.updatedAt)}
                          </span>
                        </div>
                        <p className='not-mensaje'>{t('notificaciones.respondioAlTicket', { nombre: ticket.nombre })}</p>
                      </div>
                    </Link>
                    );
                  })}
                </div>
              )}

              {/* Sin notificaciones */}
              {totalNotificaciones === 0 && (
                <div className='no-pendiente'>
                  <FiInbox />
                  <p>{t('notificaciones.todoAlDia')}</p>
                  <span>{t('notificaciones.sinNotificaciones')}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notificaciones;
